import { Divider, FormHelperText, Typography } from "@mui/material"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import type { InventoryAdjustmentHistorySearchParams } from "../schemas"

import { FilterCheckbox } from "@/components/common/Filters"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker } from "@/components/UiKit"
import type { Option } from "@/types/form-fields"

interface InventoryAdjustmentHistoryFilterDrawerProps {
  open: boolean
  searchParams: InventoryAdjustmentHistorySearchParams
  adjustmentTypeOptions: Option<string>[]
  statusOptions: Option<string>[]
  reasonOptions: Option<string>[]
  warehouseOptions: Option<string>[]
  adjustedByOptions: Option<string>[]
  onToggle: () => void
  onChange: (key: keyof InventoryAdjustmentHistorySearchParams, values: string[] | string | null) => void
  onClear: () => void
}

export const InventoryAdjustmentHistoryFilterDrawer: FC<InventoryAdjustmentHistoryFilterDrawerProps> = ({
  open,
  searchParams,
  adjustmentTypeOptions,
  statusOptions,
  reasonOptions,
  warehouseOptions,
  adjustedByOptions,
  onToggle,
  onChange,
  onClear,
}) => {
  const adjustmentTypes = searchParams.adjustmentTypes ?? []
  const statuses = searchParams.statuses ?? []
  const adjustmentReasons = searchParams.adjustmentReasons ?? []
  const warehouseAreaIds = searchParams.warehouseAreaIds ?? []
  const adjustedByIds = searchParams.adjustedByIds ?? []
  const dateFrom = searchParams.dateFrom ?? null
  const dateTo = searchParams.dateTo ?? null

  const isDateRangeInvalid = !!dateFrom && !!dateTo && dateTo < dateFrom

  const { control } = useForm({
    defaultValues: { adjustmentTypes, statuses, adjustmentReasons, warehouseAreaIds, adjustedByIds },
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='adjustment-history.filter-drawer'>
      <div className='flex h-full flex-col gap-y-4 overflow-y-auto'>
        {/* Date Range */}
        <div data-testid='adjustment-history.filter-drawer.date-range-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Date Range
          </Typography>
          <Divider className='mb-4' />
          <div className='flex flex-col gap-4 px-4'>
            <AerosDatePicker
              label='From'
              value={dateFrom}
              onChange={(value) => onChange("dateFrom", value)}
              error={isDateRangeInvalid}
              data-testid='adjustment-history.filter-drawer.date-from-field'
            />
            <AerosDatePicker
              label='To'
              value={dateTo}
              onChange={(value) => onChange("dateTo", value)}
              error={isDateRangeInvalid}
              data-testid='adjustment-history.filter-drawer.date-to-field'
            />
            {isDateRangeInvalid && <FormHelperText error>From date must be before To date</FormHelperText>}
          </div>
        </div>

        {/* Warehouse */}
        <div data-testid='adjustment-history.filter-drawer.warehouse-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Warehouse
          </Typography>
          <Divider className='mb-4' />
          <div className='px-4'>
            <div className='flex max-h-[200px] flex-col overflow-y-auto'>
              {warehouseOptions.map((option) => (
                <FilterCheckbox
                  key={option.value}
                  control={control}
                  name='warehouseAreaIds'
                  value={option.value}
                  values={warehouseAreaIds}
                  label={option.label}
                  onFilterChange={(values) => onChange("warehouseAreaIds", values)}
                  data-testid={`adjustment-history.filter-drawer.warehouse-${option.value}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Adjustment Type */}
        <div data-testid='adjustment-history.filter-drawer.adjustment-type-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Adjustment Type
          </Typography>
          <Divider className='mb-4' />
          <div className='flex flex-col px-4'>
            {adjustmentTypeOptions.map((option) => (
              <FilterCheckbox
                key={option.value}
                control={control}
                name='adjustmentTypes'
                value={option.value}
                values={adjustmentTypes as string[]}
                label={option.label}
                onFilterChange={(values) => onChange("adjustmentTypes", values)}
                data-testid={`adjustment-history.filter-drawer.adjustment-type-${option.value}`}
              />
            ))}
          </div>
        </div>

        {/* Reason */}
        <div data-testid='adjustment-history.filter-drawer.reason-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Reason
          </Typography>
          <Divider className='mb-4' />
          <div className='flex flex-col px-4'>
            {reasonOptions.map((option) => (
              <FilterCheckbox
                key={option.value}
                control={control}
                name='adjustmentReasons'
                value={option.value}
                values={adjustmentReasons as string[]}
                label={option.label}
                onFilterChange={(values) => onChange("adjustmentReasons", values)}
                data-testid={`adjustment-history.filter-drawer.reason-${option.value}`}
              />
            ))}
          </div>
        </div>

        {/* Adjusted By */}
        <div data-testid='adjustment-history.filter-drawer.adjusted-by-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Adjusted By
          </Typography>
          <Divider className='mb-4' />
          <div className='px-4'>
            <div className='flex max-h-[200px] flex-col overflow-y-auto'>
              {adjustedByOptions.map((option) => (
                <FilterCheckbox
                  key={option.value}
                  control={control}
                  name='adjustedByIds'
                  value={option.value}
                  values={adjustedByIds}
                  label={option.label}
                  onFilterChange={(values) => onChange("adjustedByIds", values)}
                  data-testid={`adjustment-history.filter-drawer.adjusted-by-${option.value}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Transaction Status */}
        <div data-testid='adjustment-history.filter-drawer.status-filter'>
          <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
            Transaction Status
          </Typography>
          <Divider className='mb-4' />
          <div className='flex flex-col px-4'>
            {statusOptions.map((option) => (
              <FilterCheckbox
                key={option.value}
                control={control}
                name='statuses'
                value={option.value}
                values={statuses as string[]}
                label={option.label}
                onFilterChange={(values) => onChange("statuses", values)}
                data-testid={`adjustment-history.filter-drawer.status-${option.value}`}
              />
            ))}
          </div>
        </div>
      </div>
    </SearchFilterDrawer>
  )
}
